import React, { useState, useEffect, memo } from "react";
import styled from "styled-components";
import { Modal, Button, Table, Tag, Tooltip } from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  CloseOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import UserDataViewer from "./UserDataViewer.jsx";

/**
 * User Details Modal
 * Shows individual user data and submitted information
 */
const UserDetailsModal = ({ visible, onClose, stepName, stepData }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("list");

  useEffect(() => {
    if (visible && stepData?.users) {
      setUsers(stepData.users);
    }
  }, [visible, stepData]);

  const columns = [
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => (
        <UserInfo>
          <PhoneOutlined />
          <span>{phone}</span>
        </UserInfo>
      ),
    },
    {
      title: "User ID",
      dataIndex: "userId",
      key: "userId",
      render: (userId) => <CodeText>{userId}</CodeText>,
    },
    {
      title: "Time in Step",
      dataIndex: "stepStartTime",
      key: "timeInStep",
      render: (startTime) => {
        const now = new Date();
        const start = new Date(startTime);
        const minutes = Math.round((now - start) / 1000 / 60);
        return (
          <TimeInfo>
            <ClockCircleOutlined />
            <span>{minutes}m</span>
          </TimeInfo>
        );
      },
    },
    {
      title: "Last Activity",
      dataIndex: "lastActivity",
      key: "lastActivity",
      render: (lastActivity) => {
        const now = new Date();
        const last = new Date(lastActivity);
        const minutes = Math.round((now - last) / 1000 / 60);
        return (
          <TimeInfo>
            <ClockCircleOutlined />
            <span>{minutes}m ago</span>
          </TimeInfo>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <ActionButtons>
          <Tooltip title="View User Details">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewUser(record)}
            >
              View
            </Button>
          </Tooltip>
        </ActionButtons>
      ),
    },
  ];

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setActiveTab("details");
  };

  const handleBackToList = () => {
    setSelectedUser(null);
    setActiveTab("list");
  };

  return (
    <Modal
      title={
        <ModalHeader>
          <UserOutlined />
          <span>Users in &quot;{stepName}&quot;</span>
          <Tag color="blue">{users.length} users</Tag>
        </ModalHeader>
      }
      open={visible}
      onCancel={onClose}
      footer={
        <ModalFooter>
          {activeTab === "details" && (
            <Button onClick={handleBackToList}>
              <ArrowLeftOutlined />
              Back to List
            </Button>
          )}
          <Button onClick={onClose}>
            <CloseOutlined />
            Close
          </Button>
        </ModalFooter>
      }
      width={activeTab === "details" ? 1000 : 800}
      centered
    >
      {activeTab === "list" ? (
        <Table
          columns={columns}
          dataSource={users}
          rowKey="userId"
          loading={false}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} users`,
          }}
          size="small"
        />
      ) : (
        <UserDataViewer user={selectedUser} stepName={stepName} />
      )}
    </Modal>
  );
};

// Styled Components
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
`;

const CodeText = styled.code`
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
`;

const TimeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #666;
  font-size: 12px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
`;

export default memo(UserDetailsModal);
